import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  CircularProgress,
  Card,
  Tooltip,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import * as XLSX from 'xlsx';
import { useQuery, gql } from '@apollo/client';

const GET_VIM_IMEI_LOGS = gql`
  query GetVimIMEILogs($limit: Int, $offset: Int) {
    getVimIMEILogs(limit: $limit, offset: $offset) {
      success
      data {
        log_id
        vehicle_id
        vin
        imei
        old_vin
        old_imei
        action
        remarks
        log_date
        eolt_completed_date
        engine_number
        vehicle_status
        vin_imei_mapped_on
        eolt_created_on
        latitude
        longitude
        pluscode
        device_timestamp
        server_timestamp
        packet_type
      }
      count
      error
    }
  }
`;

const formatEpoch = (timestamp: any): string => {
  if (!timestamp) return 'NA';
let date: Date;
  const ts = typeof timestamp === 'string' ? timestamp.trim() : timestamp;
  if (typeof ts === 'number' || !isNaN(Number(ts))) {
    const numTs = Number(ts);
    if (numTs.toString().length > 11) {
      date = new Date(numTs);           
    } else {
      date = new Date(numTs * 1000);    
    }
  } else {
    return ts;
  }
if (isNaN(date.getTime())) {
    return timestamp.toString();
  }
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const VinImeiLogs = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  const { loading, data, error, refetch } = useQuery(GET_VIM_IMEI_LOGS, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    fetchPolicy: 'cache-and-network',
  });

  const queryData = data?.getVimIMEILogs;
  const tableData = queryData?.data || [];
  const totalCount = queryData?.count || 0;

  useEffect(() => {
    refetch({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });
  }, [page, rowsPerPage, refetch]);

  const filteredData = tableData.filter((row: any) =>
    row.vin?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportToExcel = () => {
    if (!tableData.length) return;

    const formattedData = tableData.map((row: any, idx: number) => ({
      "Sr No.": page * rowsPerPage + idx + 1,
      "VIN": row.vin || 'NA',
      "IMEI": row.imei || 'NA',
      "Engine Number": row.engine_number || 'NA',
      "VIN Status": row.vehicle_status || 'NA',
      "VIN IMEI Mapped On": formatEpoch(row.vin_imei_mapped_on),
      "Created On": formatEpoch(row.eolt_created_on),
      "Latitude": row.latitude || 'NA',
      "Longitude": row.longitude || 'NA',
      "Pin Code": row.pluscode || 'NA',
      "Device TimeStamp": formatEpoch(row.device_timestamp),
      "Server TimeStamp": formatEpoch(row.server_timestamp),
      "Packet Type": row.packet_type || 'NA',
      "EOLT Completed On": formatEpoch(row.eolt_completed_date),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'VIN IMEI Logs');
    XLSX.writeFile(workbook, 'vin_imei_logs.xlsx');
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const vinColumns = [
    "Sr No.", "VIN", "IMEI", "Engine Number", "VIN Status",
    "VIN IMEI Mapped On", "Created On", "Latitude", "Longitude",
    "Pin Code", "Device TimeStamp", "Server TimeStamp",
    "Packet Type", "EOLT Completed On"
  ];

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
      }}>
        {/* Header */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
        }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" gap={2}>
            <Box display="flex" gap={1} alignItems="center" sx={{ flex: 1, maxWidth: { xs: '100%', sm: '40%' } }}>
              <TextField
                size="small"
                placeholder="Search VIN"
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton onClick={handleExportToExcel} sx={{ color: '#000' }}>
                <Tooltip title="Export to Excel" placement="top" arrow>
                  <CloudDownloadIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Table Area */}
        <Box sx={{ flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, py: 2, overflow: 'auto' }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, minWidth: 800 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                  {vinColumns.map((header) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{
                        backgroundColor: '#E60012',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                        padding: { xs: '4px', sm: '6px' },
                        borderBottom: '1px solid #e0e0e0',
                        borderRight: '1px solid #e0e0e0',
                        position: 'sticky',
                        top: 0,
                        zIndex: 11,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={vinColumns.length} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={vinColumns.length} align="center" sx={{ py: 4, color: 'red' }}>
                      Error loading data: {error.message}
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={vinColumns.length} align="center" sx={{ py: 6, color: '#888' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((row: any, idx: number) => {
                    const srNo = page * rowsPerPage + idx + 1;
                    return (
                      <TableRow key={row.log_id || idx} hover>
                        <TableCell align="center" sx={cellStyle}>{srNo}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.vin || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.imei || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.engine_number || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.vehicle_status || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpoch(row.vin_imei_mapped_on)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpoch(row.eolt_created_on)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.latitude || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.longitude || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.pluscode || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpoch(row.device_timestamp)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpoch(row.server_timestamp)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.packet_type || 'NA'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpoch(row.eolt_completed_date)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default VinImeiLogs;